import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Chip,
  Paper
} from '@mui/material';
import {
  Tabs,
  Tab,
} from '@mui/material';
import { TrendingUp } from '@mui/icons-material';
import OddsList from '../OddsList';
import { OddsListSkeleton } from '../SkeletonLoaders';
import handicapperPicksService from '../../services/handicapperPicksService';
import { useAuth } from '../../contexts/AuthContext';
import MyPicksPanel from '../handicapper/MyPicksPanel';
import BackButton from '../BackButton';

const HandicapperDashboardScreen = ({ odds, loading, error: oddsError, onLoadOdds }) => {
  const { currentUser } = useAuth();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [tabIndex, setTabIndex] = useState(0);
  const [selectedPickData, setSelectedPickData] = useState(null);
  const [analysis, setAnalysis] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Handle odds click - opens confirmation dialog with pre-populated data
  const handleOpenPickDialog = useCallback((game, outcome, market, bookmaker) => {
    if (!outcome || !market || !bookmaker) {
      console.warn('Invalid pick data');
      return;
    }

    // Determine the picked team/outcome based on market type
    let pickedTeam = '';
    let displayText = '';
    const isTotalsMarket = market === 'totals' || market === 'totals_h1' || market === 'totals_h2';
    const isSpreadsMarket = market === 'spreads' || market === 'spreads_h1' || market === 'spreads_h2';
    
    if (isTotalsMarket) {
      // For totals, the outcome name is 'Over' or 'Under'
      pickedTeam = `${outcome.name} ${outcome.point}`;
      displayText = `${outcome.name} ${outcome.point}`;
    } else if (isSpreadsMarket) {
      // For spreads, show team name with the point spread
      pickedTeam = outcome.name;
      displayText = `${outcome.name} ${outcome.point > 0 ? '+' : ''}${outcome.point}`;
    } else {
      // For moneyline (h2h)
      pickedTeam = outcome.name;
      displayText = outcome.name;
    }

    // Format pick type for display
    const pickTypeLabels = {
      'h2h': 'Moneyline',
      'spreads': 'Spread',
      'totals': 'Total'
      // Period markets temporarily hidden for development
      // 'h2h_h1': '1st Half Moneyline',
      // 'h2h_h2': '2nd Half Moneyline',
      // 'spreads_h1': '1st Half Spread',
      // 'spreads_h2': '2nd Half Spread',
      // 'totals_h1': '1st Half Total',
      // 'totals_h2': '2nd Half Total'
    };
    
    setSelectedPickData({
      game,
      outcome,
      market,
      bookmaker,
      pickedTeam,
      displayText,
      pickTypeLabel: pickTypeLabels[market] || market,
      odds: outcome.price,
      spreadPoint: outcome.point || null
    });
    
    setAnalysis('');
    setError('');
    setConfirmDialogOpen(true);
  }, []);

  const handleSubmitPick = async () => {
    // Analysis is optional, but if provided must be at least 10 characters
    if (analysis && analysis.trim().length > 0 && analysis.trim().length < 10) {
      setError('Analysis must be at least 10 characters if provided');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      // Prepare pick data
      const pickData = {
        handicapperId: currentUser.uid,
        handicapperName: currentUser.displayName || currentUser.email,
        handicapperSlug: (currentUser.displayName || currentUser.email).toLowerCase().replace(/\s+/g, '-'),
        gameId: selectedPickData.game.id,
        sport: selectedPickData.game.sport_key,
        homeTeam: selectedPickData.game.home_team,
        awayTeam: selectedPickData.game.away_team,
        pickType: selectedPickData.market,
        pickedTeam: selectedPickData.pickedTeam,
        odds: parseFloat(selectedPickData.odds),
        confidence: 'medium', // Default to medium
        analysis: analysis.trim() || 'No analysis provided',
        bookmaker: selectedPickData.bookmaker.title,
        commenceTime: selectedPickData.game.commence_time
      };

      // Create the pick in Firestore
      await handicapperPicksService.createPickV2(pickData);

      setSuccess('Pick successfully published!');
      setConfirmDialogOpen(false);
      setSelectedPickData(null);
      setAnalysis('');

      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Error submitting pick:', err);
      setError('Failed to submit pick: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Memoize OddsList to prevent unnecessary re-renders
  const memoizedOddsList = useMemo(() => {
    if (loading) {
      return <OddsListSkeleton count={4} />;
    }
    
    if (!odds || odds.length === 0) {
      return (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No odds available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Load odds to start creating picks
          </Typography>
          {onLoadOdds && (
            <Button 
              variant="contained" 
              onClick={() => onLoadOdds('upcoming')}
              sx={{ mt: 2 }}
            >
              Load Odds
            </Button>
          )}
        </Paper>
      );
    }

    return (
      <OddsList
        odds={odds}
        selectedSport={null}
        onOddsClick={handleOpenPickDialog}
        isHandicapperMode={true}
      />
    );
  }, [odds, loading, onLoadOdds, handleOpenPickDialog]);
  const handleChangeTab = (e, v) => setTabIndex(v);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <BackButton />
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <TrendingUp sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
        <Typography variant="h4" component="h1">
          Handicapper Dashboard
        </Typography>
      </Box>

      <Tabs value={tabIndex} onChange={handleChangeTab} sx={{ mb: 2 }}>
        <Tab label="Create Picks" />
        <Tab label="My Picks" />
      </Tabs>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="body1" color="text.secondary">
          Click on any odds button to create a pick. Your picks will be visible at <strong>bettoradvantage.com/handicappers/{currentUser?.displayName ? currentUser.displayName.toLowerCase().replace(/\s+/g, '-') : 'firstname-lastname'}/picks</strong>
        </Typography>
      </Paper>

      {oddsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {oddsError}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Odds List - Memoized for performance */}
      {tabIndex === 0 ? memoizedOddsList : <MyPicksPanel />}

      {/* Simplified Confirmation Dialog - Read-only except analysis (only shown on Create Picks tab) */}
      {tabIndex === 0 && (
        <Dialog
          open={confirmDialogOpen}
          onClose={() => !submitting && setConfirmDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
        <DialogTitle>Confirm Your Pick</DialogTitle>
        <DialogContent>
          {selectedPickData && (
            <>
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Game
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {selectedPickData.game.away_team} @ {selectedPickData.game.home_team}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Chip 
                    label={selectedPickData.pickTypeLabel} 
                    color="primary" 
                    size="small" 
                  />
                  <Chip 
                    label={selectedPickData.bookmaker.title} 
                    variant="outlined" 
                    size="small" 
                  />
                  <Chip 
                    label={new Date(selectedPickData.game.commence_time).toLocaleString()} 
                    variant="outlined" 
                    size="small" 
                  />
                </Box>

                <Typography variant="body1" gutterBottom>
                  <strong>Your Pick:</strong> {selectedPickData.displayText}
                </Typography>
                <Typography variant="body1">
                  <strong>Odds:</strong> {selectedPickData.odds > 0 ? `+${selectedPickData.odds}` : selectedPickData.odds}
                </Typography>
              </Paper>

              <TextField
                fullWidth
                label="Analysis (Optional)"
                multiline
                rows={6}
                value={analysis}
                onChange={(e) => setAnalysis(e.target.value)}
                placeholder="Provide your detailed analysis for this pick... (optional)"
                helperText={analysis.length > 0 ? `${analysis.length} characters (minimum 10 if provided)` : 'Optional - leave blank if you prefer'}
                autoFocus
                error={analysis.length > 0 && analysis.length < 10}
              />

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              <Alert severity="info" sx={{ mt: 2 }}>
                Once published, this pick will be visible to all users at bettorsadvantage.com/handicappers/{currentUser?.displayName ? currentUser.displayName.toLowerCase().replace(/\s+/g, '-') : 'firstname-lastname'}/picks
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDialogOpen(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitPick} 
            variant="contained"
            disabled={submitting || (analysis.length > 0 && analysis.trim().length < 10)}
          >
            {submitting ? 'Publishing...' : 'Publish Pick'}
          </Button>
        </DialogActions>
      </Dialog>
      )}
    </Container>
  );
};

export default HandicapperDashboardScreen;
